import { graphql, PageProps } from 'gatsby'
import React, { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'

import { LayoutProvider } from '@application/Contexts/LayoutContext'
import { MiddleContent } from '@application/Interfaces/Features/BlogDetails/MiddleContent/MiddleContent'
import { PageBuilder } from '@application/Interfaces/Features/PageBuilder/PageBuilder'
import type {
    HelpCenterCollectionNodeType,
    HelpCenterDetailsType,
    HelpCenterNodeType,
    HeroProfileComponentType,
    HeroSectionComponent,
    SingleTypeComponentType,
} from '@application/Types/ComponentTypes'
import type { Locale } from '@application/Types/Locale'
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react'
import { isGdprCookieAccepted } from '@infrastructure/Cookies'
import { GrowthBookFeaturesType } from '@infrastructure/Growthbook'
import { growthbookTrackingCallback } from '@infrastructure/Growthbook/utils'
import { StrapiTranslations, Translations } from '@src/Application/Types/TranslationTypes'
import { getPageUrl } from '@src/Application/Utils/generalUtils'
import { STRAPI_COMPONENT_TYPES } from '@src/Core/Constants/PageConstants'
import { HELP_CENTER_TRANSLATION } from '@src/Core/Constants/TranslationConstants'
import { GlobalAppStyles } from '@src/GlobalAppStyles'
import { Context } from '@src/Infrastructure/Context'
import { OrganizationProvider } from '@src/Infrastructure/Context/OrganizationContext'
import { localizeArticleUrl, localizeUrl } from '@src/Infrastructure/Url/localizeUrls/localizeUtilities'
import {
    getFirstIndexInnerComponent,
    getInnerComponentsForBlogDetails,
    getReadingTime,
    getTranslations,
    modifiedHeroComponents,
} from '@src/templates/utils'
export { Head } from '@src/Application/Interfaces/Features/Seo'
const growthbook = new GrowthBook<GrowthBookFeaturesType>({
    apiHost: 'https://cdn.growthbook.io',
    clientKey: `${process.env.GATSBY_GROWTHBOOK_CLIENT_KEY}`,
    subscribeToChanges: true,
    // Only required for A/B testing
    // Called every time a user is put into an experiment
    trackingCallback: growthbookTrackingCallback,
})

const Template = (
    props: PageProps<
        {
            strapiPage: HelpCenterNodeType
            strapiTranslation: StrapiTranslations
        },
        {
            language: Locale
            helpCenterCollectionNode?: HelpCenterCollectionNodeType
            singleTypeComponents: SingleTypeComponentType
        }
    >
): JSX.Element => {
    const [sendMixpanelEvents, setSendMixpanelEvents] = useState(isGdprCookieAccepted('mixpanel'))

    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                refetchOnMount: false,
                refetchOnWindowFocus: false,
                refetchOnReconnect: false,
            },
        },
    })

    const strapiTranslation = props.data.strapiTranslation

    const translations: Translations = getTranslations(strapiTranslation)

    const pageComponents = props.data.strapiPage.pageComponents ?? []
    const updateAt = props.data.strapiPage.updatedAt
    const firstCategory = props.data.strapiPage.help_center_categories[0]
    const helpCenterCollectionNode = props.pageContext.helpCenterCollectionNode
    const language = props.pageContext.language
    const collectionName = translations[language]?.['helpCenter'] || HELP_CENTER_TRANSLATION
    const pageTitle = props.data.strapiPage.title
    const showContentGuideSubmenu = props.data.strapiPage.showContentGuideSubmenu
    const readingTime = getReadingTime(pageComponents)

    const components = pageComponents.map((component, i) => {
        if (
            component.strapi_component === STRAPI_COMPONENT_TYPES.HERO_SECTION ||
            component.strapi_component === STRAPI_COMPONENT_TYPES.HERO_PROFILE
        ) {
            const castComponent = component as HeroSectionComponent | HeroProfileComponentType
            const updatedComponent = {
                ...castComponent,
                MiddleContent: (
                    <MiddleContent
                        readingTime={Math.ceil(readingTime)}
                        updatedAt={updateAt}
                        category={{ ...firstCategory, image: undefined }}
                        mode={castComponent.mode}
                    />
                ),
            }
            return updatedComponent
        }
        return component
    })

    const innerComponents = getInnerComponentsForBlogDetails(pageComponents)
    const firstIndexOfInnerComponent = getFirstIndexInnerComponent(pageComponents)

    const blogDetailsComponent: HelpCenterDetailsType = {
        id: 'blogDetails',
        strapi_component: STRAPI_COMPONENT_TYPES.BLOG_DETAILS,
        components: innerComponents,
        category: {
            ...firstCategory,
            categoryUrl: firstCategory?.language ? localizeUrl(firstCategory.language, firstCategory?.categoryUrl) : '',
            help_centers: localizeArticleUrl(firstCategory?.help_centers ?? []),
            collectionUrl: helpCenterCollectionNode ? localizeUrl(language, getPageUrl(helpCenterCollectionNode)) : '',
            collectionName: collectionName,
        },
        title: pageTitle,
        showContentGuideSubmenu,
    }

    components.splice(firstIndexOfInnerComponent, innerComponents.length, blogDetailsComponent)

    const singleTypeUSP = props.pageContext.singleTypeComponents['usp'][language]
    const singleTypeAgilitySection = props.pageContext.singleTypeComponents['agilitySection'][language]

    const modifiedPageComponents = modifiedHeroComponents({
        pageComponents: components,
        singleTypeUSP,
        singleTypeAgilitySection,
    })

    useEffect(() => {
        // Load features asynchronously when the app renders
        growthbook.loadFeatures()
    }, [])

    return (
        <Context.Provider
            value={{
                locale: language,
                translations,
                sendMixpanelEvents,
                setSendMixpanelEvents,
            }}
        >
            <OrganizationProvider>
                <QueryClientProvider client={queryClient}>
                    <LayoutProvider>
                        <GrowthBookProvider growthbook={growthbook}>
                            <GlobalAppStyles />
                            <PageBuilder
                                {...{
                                    ...props,
                                    data: {
                                        ...props.data,
                                        strapiPage: {
                                            ...props.data.strapiPage,
                                            pageComponents: modifiedPageComponents,
                                        },
                                    },
                                }}
                            />
                        </GrowthBookProvider>
                    </LayoutProvider>
                </QueryClientProvider>
            </OrganizationProvider>
        </Context.Provider>
    )
}

export default Template

export const query = graphql`
    query ($id: String!) {
        strapiPage: strapiHelpCenter(id: { eq: $id }) {
            ...StrapiHelpCenterData
        }
        strapiTranslation {
            ...StrapiTranslations
        }
    }
`
